import { useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { agreementStateLabel } from "@shared/agreementPortal";
import AgreementAttachmentLinks, {
  type AgreementAttachmentLinkRequirement,
} from "./AgreementAttachmentLinks";

type Agreement = {
  lifecycle: {
    id: string;
    state: string;
    expiresAt: string;
    recipientSignedAt: string | null;
    completedAt: string | null;
  };
  snapshot: {
    configuration: {
      title: string;
      attachments?: AgreementAttachmentLinkRequirement[];
    };
  };
};

export default function RecordAgreementHistory({
  kind,
  id,
}: {
  kind: "lead" | "rep";
  id: number;
}) {
  const [rows, setRows] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let current = true;
    void fetch("/api/agreements/status")
      .then(response => response.json())
      .then(status =>
        status.enabled
          ? fetch(
              `/api/agreements/admin/requests?recordKind=${kind}&recordId=${id}`
            ).then(response => (response.ok ? response.json() : []))
          : []
      )
      .then(data => {
        if (current) setRows(data);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [kind, id]);

  return (
    <section className="mt-5 border-t border-gray-100 pt-4">
      <div className="mb-3 flex items-center gap-2">
        <FileText className="h-4 w-4 text-[#84ab4a]" />
        <h3 className="text-sm font-bold text-gray-800">Agreement Portal</h3>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading agreements...
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400">
          No portal agreements are connected to this record.
        </p>
      ) : (
        <div className="divide-y divide-gray-100 rounded-xl border border-gray-100 bg-white">
          {rows.map(row => (
            <div
              key={row.lifecycle.id}
              className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5"
            >
              <div>
                <div className="text-xs font-semibold text-gray-800">
                  {row.snapshot.configuration.title}
                </div>
                <div className="mt-0.5 text-[11px] text-gray-400">
                  {agreementStateLabel(row.lifecycle.state)} ·{" "}
                  {new Date(
                    row.lifecycle.completedAt ??
                      row.lifecycle.recipientSignedAt ??
                      row.lifecycle.expiresAt
                  ).toLocaleDateString()}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {row.lifecycle.state === "completed" && (
                  <a
                    href={`/api/agreements/admin/requests/${row.lifecycle.id}/final`}
                    className="flex items-center gap-1 text-xs font-semibold text-[#6f923d]"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </a>
                )}
                <AgreementAttachmentLinks
                  requestId={row.lifecycle.id}
                  requirements={row.snapshot.configuration.attachments ?? []}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
